import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../layout";
import {
  type AgentWorkbenchAppearanceOptions,
  useAgentWorkbenchAppearance,
} from "./appearance";

export interface AgentWorkbenchThreadShellClassNames {
  root?: string;
  header?: string;
  title?: string;
  headerActions?: string;
  body?: string;
}

export interface AgentWorkbenchThreadShellProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title">,
    AgentWorkbenchAppearanceOptions {
  title?: ReactNode;
  headerActions?: ReactNode;
  showHeader?: boolean;
  classNames?: AgentWorkbenchThreadShellClassNames;
}

export const AgentWorkbenchThreadShell = forwardRef<
  HTMLDivElement,
  AgentWorkbenchThreadShellProps
>(function AgentWorkbenchThreadShell(
  {
    children,
    title,
    headerActions,
    showHeader = Boolean(title || headerActions),
    variant,
    density,
    className,
    classNames,
    ...shellProps
  },
  ref,
) {
  const appearance = useAgentWorkbenchAppearance({ variant, density });
  const compact = appearance.density === "compact";
  return (
    <section
      {...shellProps}
      ref={ref}
      data-agent-thread-shell="true"
      data-agent-workbench-variant={appearance.variant}
      data-agent-workbench-density={appearance.density}
      className={cn(
        "flex h-full min-h-0 min-w-0 flex-col bg-background text-foreground",
        classNames?.root,
        className,
      )}
    >
      {showHeader ? (
        <div
          className={cn(
            "flex min-w-0 items-center justify-between border-b border-border",
            compact ? "min-h-10 gap-2 px-3 py-1.5" : "min-h-11 gap-3 px-4 py-2",
            classNames?.header,
          )}
        >
          <h1
            className={cn(
              "min-w-0 flex-1 truncate font-normal text-foreground",
              compact ? "text-base leading-5" : "text-lg leading-6",
              classNames?.title,
            )}
          >
            {title}
          </h1>
          {headerActions ? (
            <div
              className={cn(
                "flex min-w-0 shrink-0 items-center gap-0.5",
                classNames?.headerActions,
              )}
            >
              {headerActions}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        className={cn("flex min-h-0 min-w-0 flex-1 flex-col", classNames?.body)}
      >
        {children}
      </div>
    </section>
  );
});
